import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './Form.css';

function Form() {
    const location = useLocation();
    const navigate = useNavigate();
    const identityNumber = location.state?.identityNumber;

    const [formData, setFormData] = useState({
        name: '',
        identity_expiry_date: '',
        mobile_number: '',
        birth_date: '',
        agency: '',
        administration: '',
        job_title: '',
        gender: 'male',
        marital_status: 'single',
        city: ''
    });

    const [dependents, setDependents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (!identityNumber) {
            console.log('❌ No identity number provided, redirecting to login...');
            navigate('/');
            return;
        }

        console.log('📄 Loading existing data for ID:', identityNumber);
        
        const loadExistingData = async () => {
            try {
                const response = await fetch(API_ENDPOINTS.GET_FORMULAIRE(identityNumber));
                const data = await response.json();

                if (data.formulaire) {
                    console.log('✅ Existing data found:', data.formulaire);
                    setFormData({
                        name: data.formulaire.name || '',
                        identity_expiry_date: data.formulaire.identity_expiry_date ? data.formulaire.identity_expiry_date.split('T')[0] : '',
                        mobile_number: data.formulaire.mobile_number || '',
                        birth_date: data.formulaire.birth_date ? data.formulaire.birth_date.split('T')[0] : '',
                        agency: data.formulaire.agency || '',
                        administration: data.formulaire.administration || '',
                        job_title: data.formulaire.job_title || '',
                        gender: data.formulaire.gender || 'male',
                        marital_status: data.formulaire.marital_status || 'single',
                        city: data.formulaire.city || ''
                    });

                    if (data.dependents && data.dependents.length > 0) {
                        console.log('✅ Found', data.dependents.length, 'dependents');
                        setDependents(data.dependents.map(dep => ({
                            name: dep.name || '',
                            identity_number: dep.identity_number || '',
                            birth_date: dep.birth_date ? dep.birth_date.split('T')[0] : '',
                            relationship: dep.relationship || ''
                        })));
                    }
                } else {
                    console.log('ℹ️ No existing data, starting fresh');
                }
            } catch (err) {
                console.error('❌ Error loading data:', err);
            }
        };

        loadExistingData();

        // Cleanup function to clear any running timers
        return () => {
            setCountdown(0);
        };
    }, [identityNumber, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDependentChange = (index, field, value) => {
        const newDependents = [...dependents];
        newDependents[index][field] = value;
        setDependents(newDependents);
    };

    const addDependent = () => {
        console.log('➕ Adding new dependent');
        setDependents([...dependents, { name: '', identity_number: '', birth_date: '', relationship: '' }]);
    };

    const removeDependent = (index) => {
        console.log('➖ Removing dependent at index:', index);
        setDependents(dependents.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name) {
            alert('الرجاء إدخال الاسم');
            console.log('❌ Name is required');
            return;
        }

        setLoading(true);
        console.log('💾 Saving form data...');
        console.log('Form data:', formData);
        console.log('Dependents:', dependents);

        try {
            const response = await fetch(API_ENDPOINTS.SAVE_FORMULAIRE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    identity_number: identityNumber,
                    formData,
                    dependents: dependents.filter(dep => dep.name.trim() !== '')
                }),
            });

            const data = await response.json();

            if (response.ok) {
                console.log('✅ Data saved successfully!');
                setSuccessMessage('تم حفظ البيانات بنجاح! ✓');
                setCountdown(15);
                
                // Start countdown
                const timer = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            console.log('⏰ Auto-logout after 15 seconds');
                            navigate('/');
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                console.error('❌ Error saving data:', data.error);
                alert('حدث خطأ أثناء حفظ البيانات');
            }
        } catch (err) {
            console.error('❌ Connection error:', err);
            alert('خطأ في الاتصال بالخادم');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="form-container">
            <div className="form-box">
                <div className="form-header">
                    <div className="header-images">
                        <img src="/images/logo_elharamain.png" alt="Logo" className="header-logo" />
                        <img src="/images/vision.png" alt="Vision 2030" className="header-logo" />  
                    </div>
                    <div className="title-section">
                        <h2>الإدارة العامة لشؤون الموارد البشرية</h2>
                        <h3>رئاسة الشؤون الدينية بالمسجد الحرام والمسجد النبوي</h3>
                    </div>
                    <div className="id-display">
                        <p>رقم الهوية: <strong>{identityNumber}</strong></p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="section-title">
                        ☑ البيانات المطلوبة للتأمين الصحي
                    </div>

                    <div className="subsection">
                        ☑ البيانات الأساسية
                    </div>

                    <div className="form-grid">
                        <div className="form-field">
                            <label>الاسم *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-field">
                            <label>رقم الهوية</label>
                            <input
                                type="text"
                                value={identityNumber}
                                disabled
                                className="disabled-field"
                            />
                        </div>

                        <div className="form-field">
                            <label>تاريخ انتهاء الهوية</label>
                            <input
                                type="date"
                                name="identity_expiry_date"
                                value={formData.identity_expiry_date}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>رقم الجوال</label>
                            <input
                                type="tel"
                                name="mobile_number"
                                value={formData.mobile_number}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>تاريخ الميلاد بالميلادي</label>
                            <input
                                type="date"
                                name="birth_date"
                                value={formData.birth_date}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>الوكالة</label>
                            <input
                                type="text"
                                name="agency"
                                value={formData.agency}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>الإدارة</label>
                            <input
                                type="text"
                                name="administration"
                                value={formData.administration}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>المسمى الوظيفي</label>
                            <input
                                type="text"
                                name="job_title"
                                value={formData.job_title}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-field">
                            <label>الجنس</label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                            >
                                <option value="male">ذكر</option>
                                <option value="female">أنثى</option>
                            </select>
                        </div>

                        <div className="form-field">
                            <label>الحالة الاجتماعية</label>
                            <select
                                name="marital_status"
                                value={formData.marital_status}
                                onChange={handleInputChange}
                            >
                                <option value="single">أعزب</option>
                                <option value="married">متزوج</option>
                                <option value="divorced">مطلق</option>
                                <option value="widowed">أرمل</option>
                            </select>
                        </div>

                        <div className="form-field">
                            <label>المدينة</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    <div className="subsection">
                        ☐ بيانات التابعين
                    </div>

                    <div className="dependents-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>الاسم</th>
                                    <th>رقم الهوية</th>
                                    <th>تاريخ الميلاد ميلادي</th>
                                    <th>صلة القرابة</th>
                                    <th>إجراء</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dependents.map((dep, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="text"
                                                value={dep.name}
                                                onChange={(e) => handleDependentChange(index, 'name', e.target.value)}
                                                placeholder="الاسم"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={dep.identity_number}
                                                onChange={(e) => handleDependentChange(index, 'identity_number', e.target.value)}
                                                placeholder="رقم الهوية"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="date"
                                                value={dep.birth_date}
                                                onChange={(e) => handleDependentChange(index, 'birth_date', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={dep.relationship}
                                                onChange={(e) => handleDependentChange(index, 'relationship', e.target.value)}
                                                placeholder="صلة القرابة"
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={() => removeDependent(index)}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button type="button" className="add-dependent-btn" onClick={addDependent}>
                            + إضافة تابع
                        </button>
                    </div>

                    <div className="declaration">
                        <p>
                            أقر أنا : <span className="name-declaration">{formData.name || '...........................................................'}</span> بأن البيانات و المعلومات المذكورة أعلاه كاملة و صحيحة وبناء
                            عليه فإن قبول الطلب سيتم على أساس هذه البيانات و للجهة الحق في استخدامها ضمن إطار
                            التأمين الصحي .
                        </p>
                    </div>

                    {successMessage && (
                        <div className="success-message">
                            <div className="success-icon">✓</div>
                            <div className="success-text">{successMessage}</div>
                            {countdown > 0 && (
                                <div className="countdown">
                                    سيتم تسجيل الخروج تلقائياً خلال <span className="countdown-number">{countdown}</span> ثانية
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="submit" className="save-btn" disabled={loading}>
                            {loading ? 'جاري الحفظ...' : 'حفظ البيانات'}
                        </button>
                        <button
                            type="button"
                            className="back-btn"
                            onClick={() => navigate('/')}
                        >
                            العودة
                        </button>
                    </div>
                </form>

                <div className="form-footer">
                    <p>@PRAGOVSA | https://prh.gov.sa | رئاسة الشؤون الدينية بالمسجد الحرام والمسجد النبوي - شارع الملك عبدالعزيز</p>
                </div>
            </div>
        </div>
    );
}

export default Form;
